import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Slider } from './ui/slider';
import { TrendingUp, DollarSign, Percent, BarChart3 } from 'lucide-react';

const PricingDemoSection = () => {
  const [devicesPerWeek, setDevicesPerWeek] = useState([25]);

  // Calculate potential profit based on devices
  const avgProfitPerDevice = 75;
  const weeklyProfit = devicesPerWeek[0] * avgProfitPerDevice;
  const monthlyProfit = weeklyProfit * 4;

  const priceComparison = [
    { platform: 'eBay', price: '$847', fee: '13%', net: '$737' },
    { platform: 'Swappa', price: '$825', fee: '3%', net: '$800' },
    { platform: 'Back Market', price: '$810', fee: '10%', net: '$729' },
    { platform: 'Apple Trade In', price: '$650', fee: '0%', net: '$650' },
    { platform: 'GameStop', price: '$575', fee: '0%', net: '$575' },
  ];

  return (
    <section id="pricing-demo" className="py-16 sm:py-24 md:py-32 relative" data-testid="pricing-demo-section">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left mb-10 sm:mb-16"
        >
          <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary mb-3 sm:mb-4 block">
            Pricing Intelligence
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6">
            See The
            <span className="text-gradient"> Difference</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl">
            Example: iPhone 15 Pro Max 256GB - See how prices compare across platforms
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
          {/* Price Comparison Table */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card p-4 sm:p-6 md:p-8"
            data-testid="price-comparison-table"
          >
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Platform Comparison
            </h3>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-4 text-xs sm:text-sm text-slate-400 pb-2 border-b border-white/10">
                <span>Platform</span>
                <span className="text-right">Price</span>
                <span className="text-right">Fee</span>
                <span className="text-right">Net</span>
              </div>
              
              {priceComparison.map((item, index) => (
                <motion.div
                  key={item.platform}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="grid grid-cols-4 items-center py-2 sm:py-3 border-b border-white/5 last:border-0"
                  data-testid={`price-row-${item.platform.toLowerCase().replace(' ', '-')}`}
                >
                  <span className="text-white font-medium text-xs sm:text-base truncate pr-2">{item.platform}</span>
                  <span className="text-right text-slate-300 text-xs sm:text-base">{item.price}</span>
                  <span className="text-right text-slate-400 text-xs sm:text-base">{item.fee}</span>
                  <span className={`text-right font-semibold text-xs sm:text-base ${index === 1 ? 'text-[#10B981]' : 'text-white'}`}>
                    {item.net}
                  </span>
                </motion.div>
              ))}
            </div>

            <div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-[#10B981]/10 border border-[#10B981]/20">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-slate-300 text-xs sm:text-base">Recommended Platform</span>
                <span className="text-[#10B981] font-bold text-sm sm:text-lg">Swappa - $800</span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">Best net value after fees</p>
            </div>
          </motion.div>

          {/* Profit Calculator */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card p-4 sm:p-6 md:p-8"
            data-testid="profit-calculator"
          >
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#10B981]" />
              Profit Calculator
            </h3>

            <div className="mb-6 sm:mb-8">
              <label className="text-slate-300 mb-3 sm:mb-4 block text-sm sm:text-base">
                How many devices do you trade in per week?
              </label>
              <Slider
                value={devicesPerWeek}
                onValueChange={setDevicesPerWeek}
                max={100}
                min={5}
                step={5}
                className="w-full"
                data-testid="devices-slider"
              />
              <div className="flex justify-between mt-2 text-xs sm:text-sm text-slate-400">
                <span>5</span>
                <span className="text-primary font-bold text-base sm:text-lg">{devicesPerWeek[0]} devices</span>
                <span>100</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-1 sm:gap-2 text-slate-400 text-xs sm:text-sm mb-1">
                  <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                  Weekly Profit
                </div>
                <div className="text-xl sm:text-2xl font-bold text-white">
                  ${weeklyProfit.toLocaleString()}
                </div>
              </div>
              
              <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-[#10B981]/10 border border-[#10B981]/20">
                <div className="flex items-center gap-1 sm:gap-2 text-[#10B981] text-xs sm:text-sm mb-1">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  Monthly Profit
                </div>
                <div className="text-xl sm:text-2xl font-bold text-[#10B981]">
                  ${monthlyProfit.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2 mb-1 sm:mb-2">
                <Percent className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-white font-medium text-sm sm:text-base">Avg. Profit Per Device</span>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-primary">${avgProfitPerDevice}</div>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">
                Based on intelligent buy/sell recommendations
              </p>
            </div>

            <p className="text-[10px] sm:text-xs text-slate-500 mt-4 sm:mt-6">
              * Calculations based on average trade-in margins. Actual results may vary.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingDemoSection;
